<script setup>
import { v4 as uuidv4 } from 'uuid'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { useHelpers } from '@/useHelpers'

const props = defineProps({
  data: {
    type: Object,
    default: () => {}
  },
  modelValue: {
    type: [String],
    default: ''
  }
})

const { formatMoney } = useHelpers()
const { t } = useI18n()

const price = computed(() => {
  return props.data.overriddenPrice || props.data.price
})

const interval = computed(() => {
  let textInterval = ''
  const length = props.data.overriddenLength || props.data.length
  switch (length) {
    case 365:
      textInterval = t('year')
      break
    default:
      textInterval = t('month')
      break
  }

  return textInterval
})

const id = computed(() => `radio-${uuidv4()}`)

const emits = defineEmits(['update:modelValue', 'onWhatContainsClick'])

const inputValue = computed({
  get() {
    return props.modelValue
  },
  set(newValue) {
    emits('update:modelValue', newValue)
  }
})
</script>

<template>
  <div class="tag" v-if="data.isFeatured || data.originalPrice || data.discountAgainst">
    <div class="tag__text">
      <template v-if="data.isBlackFriday">
        <span class="color-text-secondary">🎉 {{ $t('Black Friday Discount') }} 🎉</span>
      </template>

      <template v-else-if="data.isFeatured">
        {{ $t('Recommended') }}
      </template>

      <template v-else-if="data.discountAgainst">
        <template v-if="data.isFeatured"> | </template>
        {{ $t('You save up to discount', {discount: `${data.discountAgainst}%`}) }}
      </template>
    </div>
  </div>

  <div class="input-group">
    <div class="pricing-box__radio custom-radio-wrapper">
      <div>
        <div class="custom-radio custom-radio--medium">
          <input
            type="radio"
            v-model="inputValue"
            :value="data.code"
            :id="id"
            class="custom-radio__input"
            name="package-option"
          />
          <span class="custom-radio__indicator"></span>
        </div>
      </div>

      <div class="input-group__label-wrapper">
        <label
            :for="id"
            :class="{
              'custom-radio__label': true,
              'color-default': data.isBlackFriday
            }"
        >
          {{ data.user_label }}
        </label>
        <span class="pricing-box__price">
          <span class="sale" v-if="data.originalPrice">
            {{ formatMoney(data.originalPrice) }}
          </span>
          {{ formatMoney(price) }} / {{ interval }}
        </span>

        <div class="pricing-box__description">
          <p>{{ data.description }}</p>

          <ul v-if="data.info">
            <li v-for="(info, key) in data.info" :key="`${data.id}-${key}`">
              {{ info }}
            </li>
          </ul>
        </div>

        <a
          v-if="data.features"
          class="pricing-box__link link"
          @click="
            emits('onWhatContainsClick', {
              title: data.user_label,
              subscriptionCode: data.code,
              features: data.features
            })
          "
        >
          {{ $t('What contains subscription', { subscriptionName: data.user_label }) }}
        </a>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.tag {
  padding-left: 0;
  &:before {
    display: none;
  }
}

.custom-radio {
  margin-top: 2px;
}

.custom-radio__label {
  font-size: 22px !important;
  font-weight: 600 !important;
  font-variation-settings: "wdth" 88 !important;
}

.color-default {
  color: var(--color-tag-default) !important;
}
.color-text-secondary {
  color: var(--color-text-secondary) !important;
}
</style>